import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Tabs, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper';
import Box from '@iso/components/utility/box';
import TrainingWrapper, { TrainingGlobalStyle } from './Training.styles';
import TrainingDashboard from './components/TrainingDashboard';
import TrainingAssets from './components/TrainingAssets';
import TrainingSessions from './components/TrainingSessions';
import TrainingRecords from './components/TrainingRecords';
import ComplianceMonitoring from './components/ComplianceMonitoring';
import SearchPeople from './components/SearchPeople';
import RunSessionWizard from './components/RunSessionWizard';
import { trainingService } from '@iso/services';

const { TabPane } = Tabs;

const Training = () => {
  const activePropertyId = useSelector(state => state.property.activePropertyId);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [wizardVisible, setWizardVisible] = useState(false);
  const [permissions, setPermissions] = useState({
    has_safety_role: false,
    is_trainer: false,
    can_run_session: false,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!activePropertyId) return;
    fetchPermissions();
  }, [activePropertyId]);

  const fetchPermissions = async () => {
    try {
      const res = await trainingService.getUserPermissions(activePropertyId);
      if (res && res.data) {
        setPermissions(res.data);
      }
    } catch (e) {
      console.error('Failed to fetch training permissions:', e);
    }
  };

  const handleRunSession = () => setWizardVisible(true);
  const handleWizardClose = (saved) => {
    setWizardVisible(false);
    if (saved) setRefreshKey(k => k + 1);
  };

  const canRunSession = permissions.can_run_session;
  const hasSafetyRole = permissions.has_safety_role;

  const tabItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      children: (
        <TrainingDashboard
          propertyId={activePropertyId}
          onRunSession={handleRunSession}
          onTabChange={setActiveTab}
          canRunSession={canRunSession}
          hasSafetyRole={hasSafetyRole}
          refreshKey={refreshKey}
        />
      ),
    },
    {
      key: 'assets',
      label: 'Assets',
      children: (
        <TrainingAssets
          propertyId={activePropertyId}
          onRunSession={canRunSession ? handleRunSession : null}
        />
      ),
    },
    {
      key: 'sessions',
      label: 'Sessions',
      children: (
        <TrainingSessions
          propertyId={activePropertyId}
          onRunSession={handleRunSession}
          canRunSession={canRunSession}
          refreshKey={refreshKey}
        />
      ),
    },
    {
      key: 'records',
      label: 'Records',
      children: (
        <TrainingRecords
          propertyId={activePropertyId}
          onRunSession={handleRunSession}
          canRunSession={canRunSession}
          refreshKey={refreshKey}
        />
      ),
    },
    ...(hasSafetyRole ? [
      {
        key: 'compliance',
        label: 'Compliance',
        children: (
          <ComplianceMonitoring
            propertyId={activePropertyId}
          />
        ),
      },
      {
        key: 'people',
        label: 'Search',
        children: (
          <SearchPeople
            propertyId={activePropertyId}
          />
        ),
      },
    ] : []),
  ];

  return (
    <LayoutWrapper>
      <TrainingGlobalStyle />
      <Box style={{ padding: 0 }}>
        <TrainingWrapper>
          <div className="training-header">
            <h2>Training Module</h2>
            {canRunSession && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="btn-primary"
                onClick={handleRunSession}
              >
                Run New Training Session
              </Button>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            renderTabBar={(props, DefaultTabBar) => (
              <DefaultTabBar {...props} />
            )}
          />

          <RunSessionWizard
            visible={wizardVisible}
            propertyId={activePropertyId}
            onClose={handleWizardClose}
          />
        </TrainingWrapper>
      </Box>
    </LayoutWrapper>
  );
};

export default Training;
